import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "@/domain/constants/routes";
import { NavItem, SubNavItem, UserMenuItem } from "@/domain/models/navigation";
import { useAuth } from "@/application/services/authService";
import { useUserContext } from "@/application/services/userContextService";

/**
 * 使用導航服務
 * 提供導航相關的功能和數據
 */
export function useNavigationService() {
  const { t } = useTranslation();
  const location = useLocation();
  const { isAuthenticated, logout } = useAuth();

  /**
   * 獲取主導航項目（Sidebar 主系列）
   */
  const getNavItems = (): NavItem[] => {
    return [
      {
        label: t("home.title") || "首頁",
        path: ROUTES.HOME,
        requiresAuth: true,
        isMainNav: true,
        icon: "HomeIcon",
        children: [
          {
            label: t("home.overview") || "概覽",
            path: ROUTES.HOME,
            parentPath: ROUTES.HOME,
          },
          {
            label: t("home.activity") || "活動",
            path: ROUTES.ACTIVITY,
            parentPath: ROUTES.HOME,
          },
          {
            label: t("home.notifications") || "通知",
            path: ROUTES.NOTIFICATIONS,
            parentPath: ROUTES.HOME,
          },
        ],
      },
      {
        label: t("website.title") || "網站",
        path: ROUTES.WEBSITE,
        requiresAuth: true,
        isMainNav: true,
        icon: "GlobeAltIcon",
        children: [
          {
            label: t("website.pages") || "頁面",
            path: ROUTES.WEBSITE_PAGES,
            parentPath: ROUTES.WEBSITE,
          },
          {
            label: t("website.templates") || "模板",
            path: ROUTES.WEBSITE_TEMPLATES,
            parentPath: ROUTES.WEBSITE,
          },
          {
            label: t("website.domains") || "網域",
            path: ROUTES.WEBSITE_DOMAINS,
            parentPath: ROUTES.WEBSITE,
          },
        ],
      },
      {
        label: t("seo.title") || "SEO",
        path: ROUTES.SEO,
        requiresAuth: true,
        isMainNav: true,
        icon: "MagnifyingGlassIcon",
        children: [
          {
            label: t("seo.overview") || "概覽",
            path: ROUTES.SEO,
            parentPath: ROUTES.SEO,
          },
          {
            label: t("seo.keywords") || "關鍵字",
            path: ROUTES.SEO_KEYWORDS,
            parentPath: ROUTES.SEO,
          },
          {
            label: t("seo.performance") || "效能",
            path: ROUTES.SEO_PERFORMANCE,
            parentPath: ROUTES.SEO,
          },
        ],
      },
      {
        label: t("resources.title") || "資源",
        path: ROUTES.RESOURCES,
        requiresAuth: true,
        isMainNav: true,
        icon: "FolderIcon",
        children: [
          {
            label: t("resources.media") || "媒體",
            path: ROUTES.RESOURCES_MEDIA,
            parentPath: ROUTES.RESOURCES,
          },
          {
            label: t("resources.documents") || "文件",
            path: ROUTES.RESOURCES_DOCUMENTS,
            parentPath: ROUTES.RESOURCES,
          },
          {
            label: t("resources.uploads") || "上傳",
            path: ROUTES.RESOURCES_UPLOADS,
            parentPath: ROUTES.RESOURCES,
          },
        ],
      },
      {
        label: t("analytics.title") || "分析",
        path: ROUTES.ANALYTICS,
        requiresAuth: true,
        isMainNav: true,
        icon: "ChartBarIcon",
        children: [
          {
            label: t("analytics.overview") || "概覽",
            path: ROUTES.ANALYTICS,
            parentPath: ROUTES.ANALYTICS,
          },
          {
            label: t("analytics.traffic") || "流量",
            path: ROUTES.ANALYTICS_TRAFFIC,
            parentPath: ROUTES.ANALYTICS,
          },
          {
            label: t("analytics.conversion") || "轉換",
            path: ROUTES.ANALYTICS_CONVERSION,
            parentPath: ROUTES.ANALYTICS,
          },
        ],
      },
      {
        label: t("marketing.title") || "行銷",
        path: ROUTES.MARKETING,
        requiresAuth: true,
        isMainNav: true,
        icon: "MegaphoneIcon",
        children: [
          {
            label: t("marketing.campaigns") || "活動",
            path: ROUTES.MARKETING_CAMPAIGNS,
            parentPath: ROUTES.MARKETING,
          },
          {
            label: t("marketing.email") || "電子郵件",
            path: ROUTES.MARKETING_EMAIL,
            parentPath: ROUTES.MARKETING,
          },
          {
            label: t("marketing.social") || "社群媒體",
            path: ROUTES.MARKETING_SOCIAL,
            parentPath: ROUTES.MARKETING,
          },
        ],
      },
      {
        label: t("settings.title") || "設定",
        path: ROUTES.SETTINGS,
        requiresAuth: true,
        isMainNav: true,
        icon: "Cog6ToothIcon",
        children: [
          {
            label: t("settings.account") || "帳戶",
            path: ROUTES.SETTINGS_ACCOUNT,
            parentPath: ROUTES.SETTINGS,
          },
          {
            label: t("settings.billing") || "帳單",
            path: ROUTES.SETTINGS_BILLING,
            parentPath: ROUTES.SETTINGS,
          },
          {
            label: t("settings.team") || "團隊",
            path: ROUTES.SETTINGS_TEAM,
            parentPath: ROUTES.SETTINGS,
          },
        ],
      },
    ];
  };

  /**
   * 獲取用戶菜單項目
   * 根據用戶狀態顯示不同的選項
   */
  const getUserMenuItems = (): UserMenuItem[] => {
    const { context } = useUserContext();
    const navigate = useNavigate();

    // 創建登出並跳轉的函數
    const logoutAndRedirect = async () => {
      await logout();
      navigate(ROUTES.LOGIN);
    };

    // 如果是新用戶，只顯示登出功能
    if (context.is_new_user) {
      return [
        {
          label: t("auth.logout"),
          action: logoutAndRedirect,
          variant: "danger",
        },
      ];
    }

    // 否則顯示完整的選項
    return [
      { label: t("user.profile"), path: "/profile" },
      // { label: t('user.settings'), path: '/settings' },
      { label: t("auth.logout"), action: logoutAndRedirect, variant: "danger" },
    ];
  };

  /**
   * 檢查路徑是否為當前活動路徑
   */
  const isActive = (path: string): boolean => {
    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  /**
   * 過濾需要認證的導航項目
   */
  const getFilteredNavItems = (): NavItem[] => {
    return getNavItems().filter(
      (item) => !item.requiresAuth || isAuthenticated,
    );
  };

  /**
   * 獲取子導航項目（Navbar 子功能）
   * 根據當前路徑獲取對應的子導航項目
   */
  const getSubNavItems = (): SubNavItem[] => {
    // 判斷當前路徑屬於哪個主導航
    // 特殊處理根路徑 '/'
    const pathname = location.pathname;
    const isRootPath = pathname === "/" || pathname === "";

    // 找到匹配的主導航
    let currentMainNav = null;

    if (isRootPath) {
      // 如果是根路徑，直接返回首頁的子導航
      currentMainNav = getNavItems().find((item) => item.path === "/");
    } else {
      // 否則查找最匹配的主導航
      // 按路徑長度排序，優先匹配最長的路徑（避免 /a 匹配到 /abc 的情況）
      const sortedNavItems = [...getNavItems()].sort(
        (a, b) => b.path.length - a.path.length,
      );
      currentMainNav = sortedNavItems.find(
        (item) =>
          pathname === item.path ||
          (item.path !== "/" && pathname.startsWith(`${item.path}/`)),
      );
    }

    // 調試輸出
    console.log("getSubNavItems", {
      pathname,
      isRootPath,
      currentMainNav,
      allNavItems: getNavItems(),
    });

    // 如果找到了對應的主導航，返回其子項目
    if (currentMainNav && currentMainNav.children) {
      return currentMainNav.children.filter(
        (item) => !item.requiresAuth || isAuthenticated,
      );
    }

    // 如果沒有找到對應的主導航或沒有子項目，返回空數組
    return [];
  };

  /**
   * 獲取當前主導航項目
   */
  const getCurrentMainNav = (): NavItem | undefined => {
    // 特殊處理根路徑 '/'
    const pathname = location.pathname;
    const isRootPath = pathname === "/" || pathname === "";

    if (isRootPath) {
      // 如果是根路徑，直接返回首頁的主導航
      return getNavItems().find((item) => item.path === "/");
    } else {
      // 否則查找最匹配的主導航
      // 按路徑長度排序，優先匹配最長的路徑
      const sortedNavItems = [...getNavItems()].sort(
        (a, b) => b.path.length - a.path.length,
      );
      return sortedNavItems.find(
        (item) =>
          pathname === item.path ||
          (item.path !== "/" && pathname.startsWith(`${item.path}/`)),
      );
    }
  };

  return {
    getNavItems,
    getUserMenuItems,
    isActive,
    getFilteredNavItems,
    getSubNavItems,
    getCurrentMainNav,
  };
}
